import React from 'react';
import styled from 'styled-components';
import { recognizeReferencePoint } from '../../functions/utils/recognizeReferencePoint';
import { LauncherElementData, useLauncherElementsStore } from '../../hooks/stores/launcherElementsStore';
import { LauncherWindowData, useLauncherWindowStore } from '../../hooks/stores/launcherWindowStore';
import { UIData, useUIStore } from '../../hooks/stores/uiStore';

const Container = styled.div`
  pointer-events: none;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 9998;
`;

const GuideLine = styled.div<{ $isHorizontal: boolean }>`
  position: absolute;
  background: transparent;
  opacity: 0.8;
  z-index: 9999;

  ${(props) =>
    props.$isHorizontal
      ? `
    width: 100%;
    height: 2px;
    border-top: 1px dashed var(--primary-1);
  `
      : `
    width: 2px;
    height: 100%;
    border-left: 1px dashed var(--primary-1);
  `}
`;

const DimensionLabel = styled.div`
  pointer-events: none;
  position: absolute;
  padding: 2px 5px;
  border-radius: 5px;
  background-color: var(--neutral-2);
  color: var(--primary-1);
  font-family: 'Inter';
  font-size: 12px;
  white-space: nowrap;
  box-shadow: 0 0 5px #000;
  transform: translate(-50%, -50%);
  z-index: 10000;
`;

export const RelativePositionGuides = React.memo(() => {
  const [UIData] = useUIStore<UIData>();
  const [launcherElementsData] = useLauncherElementsStore<Record<string, LauncherElementData>>();
  const [launcherWindowData] = useLauncherWindowStore<LauncherWindowData>();


  const dimensions = React.useMemo(() => {
    // Don't show guides for multiple selections
    if (UIData.selectedElementIDs.length !== 1 || !UIData.elementHandle.isVisible || !launcherElementsData) {
      return null;
    }

    const selectedElementID = UIData.selectedElementIDs[0];
    const elementData = launcherElementsData[selectedElementID];
    if (!elementData) return null;

    // Get reference point from element's props
    const referencePoint = recognizeReferencePoint(
      (elementData.props?.class as {
        top?: string;
        left?: string;
        bottom?: string;
        right?: string;
      }) || {}
    );

    // Get element and its parent using DOM
    const element = document.getElementById(selectedElementID);
    if (!element) return null;

    // Find closest positioned parent
    let parentElement = element.parentElement;
    while (parentElement) {
      const computedStyle = window.getComputedStyle(parentElement);
      if (computedStyle.position !== 'static') {
        break;
      }
      parentElement = parentElement.parentElement;
    }

    if (!parentElement) return null;

    // Get element and parent rects for guide lines
    const elementRect = element.getBoundingClientRect();
    const parentRect = parentElement.getBoundingClientRect();
    const launcherWindow = document.getElementById('launcherWindow');
    if (!launcherWindow) return null;
    const launcherRect = launcherWindow.getBoundingClientRect();

    // Get element position - use props values if available
    const elementClass = elementData.props?.class || {};
    
    // For relative positions, use props values directly since they're already relative
    const relativeX = typeof elementClass.right === 'number' && referencePoint.x === 'right'
      ? elementClass.right  // Use right value directly if it exists and reference is right
      : typeof elementClass.left === 'number'
        ? elementClass.left  // Use left value if it exists
        : referencePoint.x === 'right'
          ? Math.floor((parentRect.right - elementRect.right) / UIData.sceneZoom)  // Compute from right edge
          : Math.floor((elementRect.left - parentRect.left) / UIData.sceneZoom);  // Compute from left edge

    const relativeY = typeof elementClass.bottom === 'number' && referencePoint.y === 'bottom'
      ? elementClass.bottom  // Use bottom value directly if it exists and reference is bottom
      : typeof elementClass.top === 'number'
        ? elementClass.top  // Use top value if it exists
        : referencePoint.y === 'bottom'
          ? Math.floor((parentRect.bottom - elementRect.bottom) / UIData.sceneZoom)  // Compute from bottom edge
          : Math.floor((elementRect.top - parentRect.top) / UIData.sceneZoom);  // Compute from top edge

    // Parent position is needed for guide lines
    const sceneParentX = Math.floor((parentRect.left - launcherRect.left) / UIData.sceneZoom);
    const sceneParentY = Math.floor((parentRect.top - launcherRect.top) / UIData.sceneZoom);
    const sceneParentWidth = Math.floor(parentRect.width / UIData.sceneZoom);
    const sceneParentHeight = Math.floor(parentRect.height / UIData.sceneZoom);

    // For guide lines, transform relative positions to window space
    const sceneX = typeof elementClass.left === 'number'
      ? Math.floor((elementClass.left + (sceneParentX * UIData.sceneZoom) / UIData.sceneZoom))
      : Math.floor((elementRect.left - launcherRect.left) / UIData.sceneZoom);

    const sceneY = typeof elementClass.top === 'number'
      ? Math.floor((elementClass.top + (sceneParentY * UIData.sceneZoom) / UIData.sceneZoom))
      : Math.floor((elementRect.top - launcherRect.top) / UIData.sceneZoom);

    // Get guide positions based on reference point
    const guideX = referencePoint.x === 'right'
      ? Math.floor(sceneX + ((typeof elementClass.width === 'number' 
          ? elementClass.width * UIData.sceneZoom
          : UIData.elementHandle.width) / UIData.sceneZoom) * UIData.sceneZoom)
      : sceneX;

    const guideY = referencePoint.y === 'bottom'
      ? Math.floor(sceneY + ((typeof elementClass.height === 'number'
          ? elementClass.height * UIData.sceneZoom
          : UIData.elementHandle.height) / UIData.sceneZoom) * UIData.sceneZoom)
      : sceneY;

    // Calculate guide line dimensions based on reference point
    const verticalLineTop = referencePoint.y === 'bottom'
      ? guideY
      : sceneParentY;

    const verticalLineHeight = referencePoint.y === 'bottom'
      ? Math.floor(sceneParentY + sceneParentHeight - guideY)
      : Math.floor(guideY - sceneParentY);

    const horizontalLineLeft = referencePoint.x === 'right'
      ? guideX
      : sceneParentX;

    const horizontalLineWidth = referencePoint.x === 'right'
      ? Math.floor(sceneParentX + sceneParentWidth - guideX)
      : Math.floor(guideX - sceneParentX);

    // Get absolute position values
    const absoluteX = referencePoint.x === 'right'
      ? Math.floor(launcherWindowData.windowWidth - (elementRect.right - launcherRect.left) / UIData.sceneZoom)
      : Math.floor((elementRect.left - launcherRect.left) / UIData.sceneZoom);

    const absoluteY = referencePoint.y === 'bottom'
      ? Math.floor(launcherWindowData.windowHeight - (elementRect.bottom - launcherRect.top) / UIData.sceneZoom)
      : Math.floor((elementRect.top - launcherRect.top) / UIData.sceneZoom);

    return {
      guideX,
      guideY,
      relativeX,
      relativeY,
      verticalLineTop,
      verticalLineHeight,
      horizontalLineLeft,
      horizontalLineWidth,
      referencePoint,
      sceneParentWidth,
      sceneParentHeight,
      sceneLauncherWidth: launcherRect.width / UIData.sceneZoom,
      sceneLauncherHeight: launcherRect.height / UIData.sceneZoom,
      showXLabel: relativeX !== 0 && relativeX !== absoluteX,
      showYLabel: relativeY !== 0 && relativeY !== absoluteY,
    };
  }, [
    UIData.selectedElementIDs,
    UIData.elementHandle.isVisible,
    UIData.elementHandle.pos,
    UIData.elementHandle.width,
    UIData.elementHandle.height,
    launcherElementsData,
  ]);

  if (!dimensions || !UIData.showRelativeGuides) return null;

  return (
    <Container>
      {/* Vertical guide line */}
      <GuideLine
        $isHorizontal={false}
        style={{
          left: dimensions.guideX,
          top: dimensions.verticalLineTop,
          height: dimensions.verticalLineHeight,
        }}
      />

      {/* Horizontal guide line */}
      <GuideLine
        $isHorizontal={true}
        style={{
          left: dimensions.horizontalLineLeft,
          top: dimensions.guideY,
          width: dimensions.horizontalLineWidth,
        }}
      />

      {/* X coordinate label */}
      <DimensionLabel
        style={{
          display: dimensions.showXLabel ? 'block' : 'none',  // Hide when 0 or matches absolute
          left: dimensions.referencePoint.x === 'right'
            ? dimensions.guideX + 5
            : dimensions.guideX + 5,
          top: dimensions.referencePoint.y === 'bottom'
            ? dimensions.sceneLauncherHeight + 10
            : -25,
          transform: 'translate(0, 0)',
        }}
      >
        x: {Math.round(dimensions.relativeX)}
      </DimensionLabel>

      {/* Y coordinate label */}
      <DimensionLabel
        style={{
          display: dimensions.showYLabel ? 'block' : 'none',  // Hide when 0 or matches absolute
          left: dimensions.referencePoint.x === 'right'
            ? dimensions.sceneLauncherWidth + 5
            : -5,
          top: dimensions.guideY + 5,
          transform: dimensions.referencePoint.x === 'right'
            ? 'translate(0, 0)'
            : 'translate(-100%, 0)',
        }}
      >
        y: {Math.round(dimensions.relativeY)}
      </DimensionLabel>
    </Container>
  );
});
